import Model from './Model';
import PlanType from '../enums/PlanType';
import { ClassTransformOptions, plainToClass, Type } from 'class-transformer';

class Subscription extends Model {
    id: number;
    establishment_id: number;
    plan_type: string;
    is_active: boolean;
    is_paid: boolean;
    @Type(() => Date)
    starts_at: Date;
    @Type(() => Date)
    ends_at: Date;
    status: string;
    @Type(() => Date)
    cancelled_at?: Date;
    @Type(() => Date)
    created_at: Date;
    @Type(() => Date)
    updated_at: Date;

    /*
    |--------------------------------------------------------------------------
    | Reflection
    |--------------------------------------------------------------------------
    */

    /**
     * Gets class name
     * survives minification/uglification
     *
     * @return {string}
     */
    getClassName(): string {
        return 'Subscription';
    }

    /*
    |--------------------------------------------------------------------------
    | Methods
    |--------------------------------------------------------------------------
    */

    /**
     * Check if subscription is valid.
     *
     * @return {boolean}
     */
    isValid(): boolean {
        if (!this.isActive()) {
            return false;
        }

        if (this.isBasic()) {
            return true;
        }

        return this.unexpired();
    }

    /**
     * Check if subscription is active.
     *
     * @return {boolean}
     */
    isActive(): boolean {
        return Boolean(this.is_active) && !this.isCancelled();
    }

    /**
     * Check if subscription is cancelled.
     *
     * @return {boolean}
     */
    isCancelled(): boolean {
        return this.status === 'cancelled';
    }

    /**
     * Check if subscription is expired.
     *
     * @return {boolean}
     */
    expired(): boolean {
        return !this.ends_at || this.ends_at.valueOf() < Date.now();
    }

    /**
     * Check if subscription is unexpired.
     *
     * @return {boolean}
     */
    unexpired(): boolean {
        return !this.expired();
    }

    /**
     * Check if subscription plan is basic.
     *
     * @return {boolean}
     */
    isBasic(): boolean {
        return this.plan_type === PlanType.BASIC;
    }

    /**
     * Checks if the subscription is free.
     *
     * @return {boolean}
     */
    isFree(): boolean {
        return this.isBasic();
    }

    /**
     * Checks if the subscription requires payment.
     *
     * @return {boolean}
     */
    isNotFree(): boolean {
        return !this.isFree();
    }

    /**
     * Converts plain (literal) object to class (constructor) object. Also works with arrays.
     */
    static new(plain: [], options?: ClassTransformOptions): Subscription[];
    static new(plain: {}, options?: ClassTransformOptions): Subscription;
    static new(plain: {} | [], options?: ClassTransformOptions): Subscription | Subscription[] {
        return plainToClass(Subscription, plain as any, options);
    }
}

export default Subscription;
